import { useUserStore } from "@/stores/user.store";
import {
  computed,
  inject,
  provide,
  reactive,
  ref,
  toValue,
  watch,
  type InjectionKey,
  type MaybeRefOrGetter,
  type UnwrapNestedRefs,
} from "vue";
import { useToastStore } from "@/stores/toast.store";
import { useI18n } from "vue-i18n";
import { useChatStore } from "../chat.store";
import type { IChatMessage, IChatMessageWithLikes } from "../model/chat-model";
import {
  CHAT_MESSAGES_PAGE_SIZE,
  mapPayloadToMessage,
  useChatStorage,
} from "./use-chat-storage";
import {
  ChatMessageEventListenerType,
  useChatRealtime,
} from "./use-chat-realtime";
import {
  useObjectTranslation,
  useTranslationStore,
} from "@/stores/translation.store";
import type { ChatRoom } from "../model/chat-room";
import type { IChatMessagePayload } from "@f3/shared/chat-types";
import { getChatEventsChannelName } from "@f3/shared/chat-types";
import { useShowStore } from "@/stores/show.store";
import { v4 as uuid } from "uuid";
import { useChannelStore } from "@/stores/channel.store";

const chatInjectionKey = Symbol("chat") as InjectionKey<
  ReturnType<typeof useProvideChat>
>;

export function useProvideChat({
  roomId,
  chatRoom,
  isChatOpen,
}: {
  roomId: MaybeRefOrGetter<string | null | undefined>;
  chatRoom?: MaybeRefOrGetter<UnwrapNestedRefs<ChatRoom> | null>;
  isChatOpen?: MaybeRefOrGetter<boolean>;
}) {
  const userStore = useUserStore();
  const toastStore = useToastStore();
  const channelStore = useChannelStore();
  const showStore = useShowStore();
  const { t } = useI18n();

  const chatEventsChannelName = computed(() => {
    if (showStore.showId) {
      return getChatEventsChannelName({
        type: "show",
        showId: showStore.showId,
      });
    }
    if (channelStore.channelSlug) {
      return getChatEventsChannelName({
        type: "channel",
        channelSlug: channelStore.channelSlug,
      });
    }
    return "";
  });

  const chatRealtimeService = useChatRealtime(roomId, chatEventsChannelName);
  const storage = useChatStorage();
  const chatStore = useChatStore();

  const resolvedChatRoom = computed(() => {
    const paramChatRoom = toValue(chatRoom);
    if (paramChatRoom) return paramChatRoom;

    const roomIdValue = toValue(roomId);
    if (!roomIdValue) return null;
    return chatStore.chatRooms.get(roomIdValue) ?? null;
  });

  const { text: chatRoomTitle } = useObjectTranslation(
    computed(() => {
      const chatRoom = resolvedChatRoom.value;
      if (!chatRoom || !chatRoom.title) return null;

      return {
        authorUserId: undefined,
        originalText: chatRoom.title,
        objectAccessors: { objectId: chatRoom.id, objectProperty: "title" },
        translatedText: chatRoom.titleTranslation,
      };
    }),
  );

  const messageRepository = computed(
    () => resolvedChatRoom.value?.chatMessages,
  );

  const pinnedMessage = computed(() => {
    if (!messageRepository.value) return null;
    return messageRepository.value.getPinnedMessage();
  });

  const likesRepository = computed(() => resolvedChatRoom.value?.chatLikes);

  const questions = computed(() => {
    const result: IChatMessage[] = [];
    if (!messageRepository.value) return result;

    for (const msg of messageRepository.value) {
      if (msg.isQuestion) {
        result.push(msg);
      }
    }
    return result.sort(
      (a, b) =>
        (likesRepository.value?.messagesLikesMap?.[b.id]?.likes ?? 0) -
        (likesRepository.value?.messagesLikesMap?.[a.id]?.likes ?? 0),
    );
  });

  const lastMessage = computed(() => {
    if (!messageRepository.value) return null;
    for (const msg of messageRepository.value.tailIterator()) {
      return msg;
    }

    return null;
  });

  function addTranslationForChatMessage(
    messageId: string,
    translation: string,
  ) {
    return messageRepository.value?.addMessageTranslation(
      messageId,
      translation,
    );
  }

  async function likeChatMessage(
    messageId: string,
    liked: boolean,
  ): Promise<boolean> {
    if (!likesRepository.value) return false;

    // Optimistically update the like status
    likesRepository.value.likeMessage(messageId, liked);

    const success = await storage.setLikeStatus(messageId, liked);

    if (!success) {
      console.error(
        `Failed to set like status for message with id ${messageId}.`,
      );
      likesRepository.value.likeMessage(messageId, !liked);
    }

    return success;
  }

  async function onRealtimeEvent(
    messageId: string | null,
    action: ChatMessageEventListenerType,
    actionById?: string,
    payload?: IChatMessagePayload,
  ): Promise<void> {
    if (!likesRepository.value || !messageRepository.value) return;

    if (action === ChatMessageEventListenerType.PIN_UPDATE) {
      messageRepository.value.pinMessage(messageId);
    } else if (!messageId) {
      console.log(
        `Received a realtime event of type ${action} without a message id.`,
      );
      return;
    } else if (action === ChatMessageEventListenerType.REMOVE) {
      const msgUserId = messageRepository.value.findById(messageId)?.author.id;

      messageRepository.value.removeMessage(messageId);
      likesRepository.value.removeMessageLikes(messageId);

      if (
        msgUserId === userStore.user?.id &&
        (!actionById || actionById !== userStore.user?.id)
      ) {
        toastStore.addToast(
          {
            title: t("ui.chat.messageDeleted"),
            message: t("ui.chat.avoidOffensiveContent"),
            type: "error",
            position: "chat",
          },
          5000,
        );
      }
    } else {
      let chatMessageWithLikes;

      if (payload) {
        chatMessageWithLikes = mapPayloadToMessage(
          payload,
          userStore.userId ?? null,
          userStore.userLanguage,
        );
      } else {
        chatMessageWithLikes = await storage.fetchMessage(messageId);
      }

      if (!chatMessageWithLikes) {
        console.log(`Message with id ${messageId} not found.`);
        return;
      }

      messageRepository.value.appendMessage(chatMessageWithLikes.message);
      likesRepository.value.updateMessageLikeMetadata(
        messageId,
        chatMessageWithLikes.likes,
      );
    }
  }

  function makeTemporaryChatMessage(
    content: string,
    isQuestion: boolean,
    replyToId?: string | null | undefined,
  ): IChatMessageWithLikes | null {
    const temporaryMessageId = uuid();

    const userData = userStore.user;
    const chatRoomId = resolvedChatRoom.value?.id;

    if (!userStore.userId || !userData || !chatRoomId) return null;

    const replyTo = replyToId
      ? (messageRepository.value?.findById(replyToId) ?? null)
      : null;

    return {
      likes: {
        isLiked: false,
        likes: 0,
      },
      message: {
        id: temporaryMessageId,
        content,
        isQuestion,
        messageTranslation: undefined,
        isVisible: true,
        isVerified: false,
        isPinned: false,
        timestamp: new Date().toISOString(),
        chatRoomId: chatRoomId,
        replyTo: replyTo ?? undefined,
        author: {
          id: userData.id,
          username: userData.username,
          avatar: userData.avatar ?? undefined,
          countryIso: userData.country_iso,
          hasPaidMembership: channelStore.isSuperfan,
        },
      },
    };
  }

  async function sendChatMessage(
    message: string,
    isQuestion: boolean,
    replyToId: string | null,
  ): Promise<void> {
    const roomId = resolvedChatRoom.value?.id;
    if (!roomId) {
      console.log(
        `Unable to send message to chat room without a chat room id.`,
      );
      return;
    }

    const temp = makeTemporaryChatMessage(message, isQuestion, replyToId);

    try {
      if (temp) messageRepository.value?.appendMessage(temp.message);

      const chatMessageWithLikes = await storage.persistMessage({
        message,
        isQuestion,
        chatRoomId: roomId,
        replyToId: replyToId ?? undefined,
      });

      if (!chatMessageWithLikes) {
        if (temp) messageRepository.value?.removeMessage(temp.message.id);

        console.log(`Unable to send message with body "${message}".`);
        return;
      } else {
        if (temp) {
          messageRepository.value?.replaceMessage(
            chatMessageWithLikes.message,
            temp.message.id,
          );

          // Update lastSeenMessageId to the real message ID after successful send
          if (resolvedChatRoom.value?.lastSeenMessageId === temp.message.id) {
            resolvedChatRoom.value.lastSeenMessageId =
              chatMessageWithLikes.message.id;
          }
        } else {
          messageRepository.value?.appendMessage(chatMessageWithLikes.message);
        }
      }

      chatRealtimeService.sendChatMessage(chatMessageWithLikes.message);
    } catch (_error) {
      if (temp) messageRepository.value?.removeMessage(temp.message.id);
    }
  }

  const hasMoreMessagesMap = ref<Map<string, boolean>>(new Map());

  function hasMoreMessagesForRoom(roomId: string | null | undefined): boolean {
    if (!roomId) return false;
    return hasMoreMessagesMap.value.get(roomId) ?? false;
  }

  async function fetchMessagesForRoom(
    roomId: string,
    limit?: number,
  ): Promise<IChatMessageWithLikes[] | null> {
    // If a previous page proved history is exhausted, avoid another fetch.
    if (hasMoreMessagesMap.value.get(roomId) === false) return null;

    const pageSize = limit ?? CHAT_MESSAGES_PAGE_SIZE;
    const chatMessagesWithLikes = await storage.fetchMessagesForRoom(
      roomId,
      pageSize,
    );

    hasMoreMessagesMap.value.set(
      roomId,
      (chatMessagesWithLikes?.length ?? 0) >= pageSize,
    );

    if (!messageRepository.value || !likesRepository.value) return null;

    if (!chatMessagesWithLikes) {
      console.log(`No messages found for room with id ${roomId}.`);
      return null;
    }

    messageRepository.value.prependMessages(
      chatMessagesWithLikes.map((m) => m.message),
    );

    for (const chatMessageWithLikes of chatMessagesWithLikes) {
      if (chatMessageWithLikes.message.isPinned) {
        messageRepository.value.pinMessage(chatMessageWithLikes.message.id);
      }
      likesRepository.value.updateMessageLikeMetadata(
        chatMessageWithLikes.message.id,
        chatMessageWithLikes.likes,
      );
    }

    return chatMessagesWithLikes;
  }

  // Fetch the first page as soon as the chat room resolves — independent of
  // which view is mounted (fan/host, mobile/desktop) or when it mounts. The
  // mobile collapsed-chat preview reads chat.lastMessage, which stays empty
  // until the first page loads; previously mobile only fetched on chat-open
  // so the preview showed nothing until a realtime message arrived. The room
  // can resolve after a view's onMounted, so a one-shot mount fetch misses it;
  // an immediate watch fires whenever the id becomes available.
  // Idempotent via hasMoreMessagesMap.
  watch(
    () => resolvedChatRoom.value?.id,
    (roomId) => {
      if (roomId) void fetchMessagesForRoom(roomId);
    },
    { immediate: true },
  );

  async function setMessagePinnedState(messageId: string, value: boolean) {
    try {
      await storage.setMessagePinnedState(messageId, value);
      chatRealtimeService.sendChatMessagePinUpdate(
        value ? messageId : null,
        userStore.user!.id!,
      );
    } catch (error) {
      console.error(
        `[ChatService] Failed to pin message with id ${messageId}.`,
        error,
      );
      toastStore.addToast(
        {
          title: t("ui.chat.failedToPinMessage"),
          message: t("ui.chat.contactSupportIfPersists"),
          type: "error",
          position: "chat",
        },
        5000,
      );
    }
  }

  async function deleteChatMessage(messageId: string) {
    try {
      await storage.removeMessage(messageId);
      chatRealtimeService.sendChatMessageRemove(messageId, userStore.user!.id!);
    } catch (error) {
      console.error(
        `[ChatService] Failed to delete message with id ${messageId}.`,
        error,
      );
      toastStore.addToast(
        {
          title: t("ui.chat.failedToDeleteMessage"),
          message: t("ui.chat.contactSupportIfPersists"),
          type: "error",
          position: "chat",
        },
        5000,
      );
    }
  }

  async function reportChatMessage(
    messageId: string,
    reason?: string,
  ): Promise<"success" | "duplicate" | "self_report" | "error"> {
    const result = await storage.reportMessage(messageId, reason);

    if (result.type === "success") {
      toastStore.addToast(
        {
          message: t("channel.chat.report.success"),
          type: "success",
          position: "chat",
        },
        5000,
      );
    } else if (result.type === "duplicate") {
      toastStore.addToast(
        {
          message: t("channel.chat.report.duplicate"),
          type: "info",
          position: "chat",
        },
        5000,
      );
    } else if (result.type === "self_report") {
      toastStore.addToast(
        {
          message: t("channel.chat.report.selfReport"),
          type: "error",
          position: "chat",
        },
        5000,
      );
    } else {
      toastStore.addToast(
        {
          message: t("channel.chat.report.error"),
          type: "error",
          position: "chat",
        },
        5000,
      );
    }

    return result.type;
  }

  chatRealtimeService.watchChatMessages(
    (message, action, actionById) => {
      onRealtimeEvent(message?.id ?? null, action, actionById, message.payload);
    },
    (event) => {
      const translation = event.translations[userStore.userLanguage] ?? null;
      if (!translation) return;
      addTranslationForChatMessage(event.chat_message_id, translation);
      messageRepository.value?.addReplyToTranslation(
        event.chat_message_id,
        translation,
      );
    },
    (delta) => {
      if (!likesRepository.value) return;
      likesRepository.value.setAbsoluteCount(delta.message_id, delta.total);
    },
  );

  const translationStore = useTranslationStore();
  const lastVerifiedMessage = computed(() => {
    const autoTranslateOn = translationStore.autoTranslateOn;

    if (!messageRepository.value) return null;

    for (const msg of messageRepository.value.tailIterator()) {
      const hasTranslation = msg.messageTranslation;
      const authoredBySelf = msg.author.id === userStore.userId;

      if (!msg.isVisible) {
        continue;
      }

      if (authoredBySelf) {
        return msg;
      }

      if (autoTranslateOn && !hasTranslation) {
        continue;
      }

      return msg;
    }

    return null;
  });

  const unreadCount = computed(() => {
    if (!messageRepository.value || !resolvedChatRoom.value) return 0;

    let count = 0;
    const lastSeenMessageId = resolvedChatRoom.value.lastSeenMessageId;
    const autoTranslateOn = translationStore.autoTranslateOn;

    if (!lastSeenMessageId) return 0;

    // Only count messages when:
    // 1. Are not authored by the current user
    // 2. Have a translation if auto-translate is on
    // 3. Don't need translation if auto-translate is off
    const isUnreadMessage = (message: IChatMessage) =>
      message.author.id !== userStore.userId &&
      message.isVisible &&
      ((autoTranslateOn && message.messageTranslation) || !autoTranslateOn);

    for (const message of messageRepository.value.tailIterator()) {
      if (message.id === lastSeenMessageId) break;

      if (isUnreadMessage(message)) {
        count++;
      }
    }

    return count;
  });

  function markMessagesAsRead() {
    if (!resolvedChatRoom.value || !lastVerifiedMessage.value) return;
    resolvedChatRoom.value.lastSeenMessageId = lastVerifiedMessage.value.id;
  }

  function clear() {
    messageRepository.value?.clear();
    likesRepository.value?.clear();
    hasMoreMessagesMap.value.clear();
  }

  // Mark messages as read when the chat is opened
  watch(
    () => toValue(isChatOpen),
    (isOpen) => {
      if (isOpen) markMessagesAsRead();
    },
  );

  // If the chat is opened, mark messages as read immediately
  watch(lastVerifiedMessage, () => {
    if (toValue(isChatOpen)) {
      markMessagesAsRead();
    }
  });

  const definition = reactive({
    chatRoomTitle,
    resolvedChatRoom,
    messages: messageRepository,
    pinnedMessage,
    likes: likesRepository,
    questions,
    lastMessage,
    lastVerifiedMessage,
    unreadCount,
    markMessagesAsRead,
    likeChatMessage,
    sendChatMessage,
    fetchMessagesForRoom,
    hasMoreMessagesForRoom,
    deleteChatMessage,
    reportChatMessage,
    setMessagePinnedState,
    addTranslationForChatMessage,

    watchChatMessages: chatRealtimeService.watchChatMessages,

    clear,
  });

  provide(chatInjectionKey, definition);

  return definition;
}

export function useChat() {
  const value = inject(chatInjectionKey);

  if (!value) {
    throw new Error(
      `In order to use "useChat" you need to provide it first above the component tree via "useProvideChat"!`,
    );
  }

  return value;
}
